import FormattedMessage from 'I18n/components/FormattedMessage';
import { memo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { IS_DEAL_CLOSED } from '../v2/constants/AdditionalPropertyNames';
import { useAdditionalPropertyValues } from '../v2/hooks/useAdditionalPropertyValues';
import { useGetHasScope } from '../v2/hooks/useGetHasScope';
import PropertyInputNumber from './PropertyInputNumber';
import { AUTOMATION_INPUT_MODE, BULK_INPUT_MODE, PREVIEW_INPUT_MODE } from '../v2/types/PropertyInputV2Component';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledRow = styled.div.withConfig({
  displayName: "PropertyInputDealExchangeRateWrapper__StyledRow"
})(["display:flex;align-items:center;"]);
const hasValue = value => {
  return value !== undefined && value !== null && value !== '';
};

// Exchange rate is being made editable, but only in limited cases. The
// property is changing from a calculated property to an editable property
// that is updated by a backend system. However, the automatic updates will
// only be to open deals. Any other updates will be rejected by the backend.
// If the deal is closed or the input is in bulk edit mode, then we will allow
// multi-currency-write to edit the exchange rate. The framework doesn't support this
// well now so we will:
//   * Make the input read-only if we know the deal is open
//   * Add a help icon letting the user know when the exchange rate is editable
//     and by whom. This icon is only shown if the property is editable and has
//     a value.
// In the case of bulk edit, we will rely on the backend to enforce the open vs
// closed checks.
const PropertyInputDealExchangeRateWrapper = props => {
  useComponentMountMetric('PropertyInputDealExchangeRateWrapper');
  const getHasScope = useGetHasScope();
  const {
    data: additionalValues,
    loading: additionalValuesLoading
  } = useAdditionalPropertyValues({
    isDealClosed: IS_DEAL_CLOSED
  });
  const [focused, setFocused] = useState(false);
  const {
    inputMode,
    onBlur,
    onFocus
  } = props;
  const value = usePropertyValue();
  const handleFocus = useCallback(e => {
    setFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus]);
  const handleBlur = useCallback(e => {
    setFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);
  if (additionalValuesLoading || additionalValues === undefined) {
    return null;
  }
  const {
    isDealClosed
  } = additionalValues;
  const isPreview = inputMode === PREVIEW_INPUT_MODE;
  const showHelpIcon = !isPreview && !props.readOnly && (hasValue(value) || focused);
  const isBulkEdit = inputMode === BULK_INPUT_MODE;
  const isAutomation = inputMode === AUTOMATION_INPUT_MODE;
  const isExchangeRateEditable = isPreview || (isDealClosed === 'true' || isBulkEdit || isAutomation) && getHasScope && getHasScope('multi-currency-write');
  return /*#__PURE__*/_jsxs(StyledRow, {
    children: [showHelpIcon && /*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputDealExchangeRateWrapper.help"
      }),
      tooltipPlacement: "top right",
      marginInlineEnd: "100"
    }), /*#__PURE__*/_jsx(PropertyInputNumber, Object.assign({}, props, {
      onBlur: handleBlur,
      onFocus: handleFocus,
      readOnly: props.readOnly || !isExchangeRateEditable
    }))]
  });
};
export default /*#__PURE__*/memo(PropertyInputDealExchangeRateWrapper);